// The reporting-window control, shared by every Seller Intelligence page that
// has one.
//
// WHY THIS IS ONE COMPONENT. Six pages each carried their own copy of the same
// From/To/Apply block, which meant one defect existed in six places: the inputs
// and the cards below them are separately stateful, so editing a date left the
// control showing one period while every card kept measuring another, with
// nothing on screen saying so. A page that reads "1 Oct 2025 – 26 Sept 2026"
// above cards measuring thirty days looks exactly like a bug whether or not it
// is one.
//
// So this control refuses to be quiet about that state. Apply is disabled when
// there is nothing to apply, it says "Applying…" until the last component has
// finished rather than guessing at a duration, and while an edit is unapplied
// it names the period actually on screen.
//
// The PRD for these pages requires "consistent application of the window
// control across every component". Keeping the commit step is a deliberate
// departure — the product table is expensive enough that refetching per
// keystroke is worse — so the trade is that the uncommitted state is always
// visible rather than inferred.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';

import local from './WindowControl.module.css';

// Mirrors MAX_WINDOW_DAYS in the backend services. Past this the request is
// refused, so the control stops it here where the limit can be explained.
export const MAX_WINDOW_DAYS = 730;
const DEFAULT_WINDOW_DAYS = 30;

const todayIso = () => new Date().toISOString().slice(0, 10);

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const readable = (iso) => new Date(`${iso}T00:00:00`)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// Inclusive, so a start and end on the same day is one day rather than none.
const spanDays = ({ start, end }) => {
  if (!start || !end) return 0;
  const ms = new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`);
  return Math.round(ms / 86400000) + 1;
};

// Holds the window a page is showing, the window its inputs are offering, and
// which of its components are still loading.
export const useWindowControl = ({ days = DEFAULT_WINDOW_DAYS } = {}) => {
  const initial = useMemo(
    () => ({ start: isoDaysAgo(days - 1), end: todayIso() }), [days]);
  const [range, setRange] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [busy, setBusy] = useState({});

  const setWidgetBusy = useCallback((id, isBusy) => {
    setBusy((prev) => (prev[id] === isBusy ? prev : { ...prev, [id]: isBusy }));
  }, []);

  // One stable callback per component id. Stability matters: a child keeps this
  // in its effect dependencies, so a new function each render would put it in a
  // refetch loop.
  const trackers = useRef({});
  const track = useCallback((id) => {
    if (!trackers.current[id]) {
      trackers.current[id] = (isBusy) => setWidgetBusy(id, isBusy);
    }
    return trackers.current[id];
  }, [setWidgetBusy]);

  const span = spanDays(range);
  const invalid = !range.start || !range.end || range.end < range.start;
  const tooLong = span > MAX_WINDOW_DAYS;

  const control = {
    range,
    applied,
    span,
    invalid,
    tooLong,
    applying: Object.values(busy).some(Boolean),
    unapplied: range.start !== applied.start || range.end !== applied.end,
    setStart: (start) => setRange((prev) => ({ ...prev, start })),
    setEnd: (end) => setRange((prev) => ({ ...prev, end })),
    track,
  };
  control.apply = () => {
    if (control.invalid || control.tooLong) return;
    setApplied({ start: range.start, end: range.end });
  };
  return control;
};

// A page-specific control sitting in the same row, styled to match. Exported so
// a page can add one without reaching for the window control's stylesheet.
export const WindowField = ({ label, children }) => (
  <label className={local.field}>
    <span>{label}</span>
    {children}
  </label>
);

// `children` are extra controls for this page, rendered between the dates and
// Apply — Sales Performance puts its granularity selector there.
export const WindowControl = ({ control, children }) => {
  const { range, applied, applying, unapplied, invalid, tooLong, span } = control;
  const blocked = invalid || tooLong;

  return (
    <div className={local.controls}>
      <label className={local.field}>
        <span>From</span>
        <input type="date" value={range.start} max={range.end}
          onChange={(e) => control.setStart(e.target.value)} />
      </label>
      <label className={local.field}>
        <span>To</span>
        <input type="date" value={range.end} min={range.start}
          onChange={(e) => control.setEnd(e.target.value)} />
      </label>

      {children}

      <button type="button" className={local.primary}
        onClick={control.apply}
        disabled={applying || !unapplied || blocked}>
        <FiRefreshCw className={applying ? local.spin : undefined} />
        {applying ? 'Applying…' : 'Apply'}
      </button>

      {tooLong && (
        <span className={local.invalid}>
          That is {span} days. The longest period these reports can answer is
          {' '}{MAX_WINDOW_DAYS}.
        </span>
      )}

      {!blocked && unapplied && !applying && (
        <span className={local.pending}>
          Showing {readable(applied.start)} – {readable(applied.end)}.
          Press Apply to rebuild for the dates above.
        </span>
      )}
    </div>
  );
};

export default WindowControl;
